<template>
    <modal-detail-mdw-line-group
    :headerColor="detailInfo.headerStyles.color"
    :title="detailInfo.typeDescription"
    :subTitle="detailInfo.subTitle"
    :icon="detailInfo.headerStyles.icon"
    :buttonsTopData="detailInfo.operationHasReceipt ? buttonsTopData : null"
    :requestPayload="payloadDetailModal"
    :detailRequestAction="requestAction"
    :errorStateMessage="t('approvals.signed.modal.details.emptyStates.errorStateMessage')"
    :showFooter="showFooter"
    :buttonsFooterData="buttonsFooterData"
    :statusIconSvgName="detailInfo.status.icon ? detailInfo.status.icon : 'clock'"
    :statusIconColor="detailInfo.status.classes ? detailInfo.status.classes : 'warning'"
    :statusDescription="detailInfo.status.description"
    :triggerClose="triggerClose"
    sizeClass="big"
    subTitleIcon="arrow-down-full"
    forceNegativeAmount
    showBottomContent
    @hideMe="emit('hideMe')">
        <template v-slot:bottomContent>
            <div class="signatures-container" :class="{ mobile: isMobile }">
                <!-- Topups To Submit Divider -->
                <divider-generic
                v-if="detailInfo.operationTypeId === 500001"
                icon="card-recharge"
                dividerSize="medium"
                :title="t('approvals.pendingSignature.modal.details.topupsToSubmit.title')"/>
                <!-- Topup Submission Table -->
                <topups-to-submit-table
                v-if="detailInfo.operationTypeId === 500001"
                :topupsToSubmit="detailInfo.topupPrepaidCreditCardBatchFileSubmission.fileEntries"
                :isMobile="isMobile" />
                <!-- Signatures Divider -->
                <divider-generic
                icon="document-edit"
                dividerSize="medium"
                :applyBorder="isMobile ? detailInfo.operationTypeId !== 500001 : true"
                :title="t('approvals.signed.modal.details.signatures.title')"/>
                <!-- Signatures Chart -->
                <div class="percentage-chart-block">
                    <div class="chart-container">
                        <!-- Chart -->
                        <chart-simple :graphData="detailInfo.graphData" :colorsArray="getAppThemeMode === 'light' ? ['#00894E', '#DDDDDE'] : ['#5E977E', '#4A4A4A']"/>
                    </div>
                    <!-- Signed Percentage -->
                    <div v-if="detailInfo.signedPercentage !== 0" class="signed-percentage">
                        <span class="percentage">{{ detailInfo.signedPercentage }}%</span>
                        <div class="label">{{ t('approvals.signed.modal.details.signatures.signedChartLabel') }}</div>
                    </div>
					<!-- Not Signed Percentage -->
                    <div v-if="detailInfo.notSignedPercentage !== 0" class="not-signed-percentage">
                        <span class="percentage">{{ detailInfo.notSignedPercentage }}%</span>
                        <div class="label">{{ t('approvals.signed.modal.details.signatures.notSignedChartLabel') }}</div>
                    </div>
                </div>
                <!-- Signatures Table -->
                <signatures-table :signatureList="detailInfo.signatureList" :isMobile="isMobile" />
            </div>
        </template>
    </modal-detail-mdw-line-group>
</template>

<script setup>
import ModalDetailMdwLineGroup from '@/components/generic/modals/side/DetailMdwLineGroup'
import DividerGeneric from '@/components/generic/dividers/Generic'
import ChartSimple from '@/components/generic/charts/Simple'
import SignaturesTable from '@/components/features/approvals/details/SignaturesTable'
import TopupsToSubmitTable from '@/components/features/approvals/details/TopupsToSubmitTable'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/** Props
@detailsInfo {Object} - info for the details modal
@payloadDetailModal {Object} - object with payload for the details request
@requesAction {String} - details request action
@buttonsTopData {Object} - data for the top buttons
@showFooter {Boolean} - prop to control display of footer
@buttonsFooterData {Array} - data for the footer buttons
@triggerClose {Boolean} - prop to trigger close of filter modal so animation happens before our modal disapears
@isMobile {Boolean}
*/
const props = defineProps({
    detailInfo: Object,
    payloadDetailModal: Object,
    requestAction: String,
    buttonsTopData: Object,
    showFooter: Boolean,
    buttonsFooterData: Array,
    triggerClose: Boolean,
    isMobile: Boolean
})

const emit = defineEmits(['hideMe'])

const { getAppThemeMode } = mapGetters()
</script>

<style lang="scss" scoped>
.signatures-container {
    display: flex;
    flex-direction: column;

    gap: 24px;
    .percentage-chart-block {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 24px;

        height: 88px;

        margin-bottom: 10px;

        .chart-container {
            width: 88px;
            height: 88px;
            position: relative;
        }

        .signed-percentage,
        .not-signed-percentage {
            display: flex;
            flex-direction: column;
            gap: 10px;

            .percentage {
                @include H2-Medium;
                color: var(--secondary);
            }

            .label {
                @include H7-Regular;
                color: var(--secondary-dark-1);
            }
        }

        .signed-percentage {
            .percentage {
                color: var(--positive);
            }
        }
    }

    &.mobile {
        border-radius: 12px;

        background-color: var(--white);
        box-shadow: 0 10px 16px 0 rgba(0, 50, 90, 0.08);

        :deep(.divider-generic-wrapper) {
            margin-left: 16px;
        }

        .percentage-chart-block {
            .chart-container {
                margin-left: 16px;
            }
        }
    }
}
</style>
